<script setup lang="ts">
import { computed } from 'vue'
import type { SwitchRootEmits, SwitchRootProps } from 'reka-ui'
import type { HTMLAttributes } from 'vue'
import { reactiveComputed, reactiveOmit } from '@vueuse/core'
import {
  SwitchRoot,
  SwitchThumb,
  useForwardPropsEmits,
} from 'reka-ui'
import { cn } from '@ds/lib/utils'
import { SIZE_CLASS, type SwitchSize } from '.'

const props = defineProps<SwitchRootProps & { class?: HTMLAttributes['class'], size?: SwitchSize }>()

const emits = defineEmits<SwitchRootEmits>()

const delegatedProps = reactiveOmit(props, 'class', 'size')

// SwitchRoot is generic and declares `value?: string` (the form submission
// value). v-bind of a ComputedRef makes the template checker match that
// ref's `.value` against the string prop. toReactive keeps the ComputedRef
// type (UnwrapNestedRefs does not unwrap a top-level ref), so read `.value`
// inside reactiveComputed and bind the plain props object.
const forwardedProps = useForwardPropsEmits(delegatedProps, emits)
const forwarded = reactiveComputed(() => forwardedProps.value)

const sizeClass = computed(() => SIZE_CLASS[props.size ?? 'md'])
</script>

<template>
  <SwitchRoot
    v-bind="forwarded"
    :class="cn(
      // The border creates the 3px inset (constant across sizes — see
      // SIZE_CLASS's own comment), so the thumb needs no absolute positioning.
      'peer inline-flex shrink-0 cursor-pointer items-center rounded-full border-[3px] border-transparent transition-colors duration-ds-normal ease-ds-standard focus-visible:outline-none focus-visible:shadow-ds-focus disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=unchecked]:bg-input',
      sizeClass.root,
      props.class,
    )"
  >
    <SwitchThumb
      :class="cn(
        'pointer-events-none block rounded-full bg-background shadow-ds-sm ring-0 transition-transform duration-ds-normal ease-ds-standard',
        sizeClass.thumb,
      )"
    >
      <slot name="thumb" />
    </SwitchThumb>
  </SwitchRoot>
</template>
